import { Schema, SchemaSettings } from '@orbit/data'
import { KC } from '@/http/utils'
import { PatientWithEventsEvent } from '@/jsonapi/PatientWithEvents'
import { DataPoint, InhalerCompliance, InhalerProduct } from '@/jsonapi/CommonTypes'

enum ModelTypes {
  PatientWithStats = 'patient-with-stat',
}

enum ModelTypeNames {
  PatientWithStats = 'patientWithStat',
}

export enum PatientStatsInhalerTypes {
  Maintenance = 'maintenance',
  Reliever = 'reliever',
}

export interface PatientWithStatsInhalerUsage {
  uid: string
  name: string
  type?: PatientStatsInhalerTypes
  aigNum: string
  serialNumber: string
  dataPoints: DataPoint[]
}

export interface PatientWithStatsAttributes {
  timezone: string
  firstName: string
  lastName: string
  stepCounts: DataPoint[]
  numberOfAwakenings: DataPoint[]
  inhalerUsages: PatientWithStatsInhalerUsage[]
  inhalerCompliances: InhalerCompliance[]
  assignedInhalers: InhalerProduct[]
  activeInhalers: InhalerProduct[]
  inhalerUsagesDaily: PatientWithStatsInhalerUsage[]
  controlledDays: number
  uncontrolledDays: number
  startTimestamp: number | null | undefined
  endTimestamp: number | null | undefined
  resultStartTimestamp: number | null | undefined
  resultEndTimestamp: number | null | undefined
  consolidatedDailyEvents: PatientWithEventsEvent[]

  bodyWeights: DataPoint[]
  bodyTemperatures: DataPoint[]
  bodyWeightsRollingAvg: DataPoint[]
  bodyTemperaturesRollingAvg: DataPoint[]
  bodyWeightsDailyAvg: DataPoint[]
  bodyTemperaturesDailyAvg: DataPoint[]
  bodyWeightsLastOneDaily: DataPoint[]
  bodyTemperaturesLastOneDaily: DataPoint[]
  oxygenSaturations: DataPoint[]
  pulseRates: DataPoint[]
  perfusionIndexes: DataPoint[]
  bloodPressureSystolics: DataPoint[]
  bloodPressureDiastolics: DataPoint[]
  casualWalkDurationsPerDay: DataPoint[]
}

export interface PatientWithStatsSchema {
  id: string
  attributes: PatientWithStatsAttributes
}

const schemaDefinition: SchemaSettings = {
  models: {
    [ModelTypeNames.PatientWithStats]: {
      attributes: {
        timezone: { type: 'string' },
        firstName: { type: 'string' },
        lastName: { type: 'string' },
        stepCounts: { type: 'array' },
        numberOfAwakenings: { type: 'array' },
        assignedInhalers: { type: 'array' },
        activeInhalers: { type: 'array' },
        inhalerUsages: { type: 'array' },
        inhalerUsagesDaily: { type: 'array' },
        inhalerCompliances: { type: 'array' },
        controlledDays: { type: 'number' },
        uncontrolledDays: { type: 'number' },
        starttimestamp: { type: 'number' },
        endtimestamp: { type: 'number' },
        resultStartTimestamp: { type: 'number' },
        resultEndTimestamp: { type: 'number' },
        consolidatedDailyEvents: { type: 'array' },
        bodyWeights: { type: 'array' },
        bodyTemperatures: { type: 'array' },
        bodyWeightsRollingAvg: { type: 'array' },
        bodyTemperaturesRollingAvg: { type: 'array' },
        bodyWeightsDailyAvg: { type: 'array' },
        bodyTemperaturesDailyAvg: { type: 'array' },
        bodyWeightsLastOneDaily: { type: 'array' },
        bodyTemperaturesLastOneDaily: { type: 'array' },
        oxygenSaturations: { type: 'array' },
        pulseRates: { type: 'array' },
        perfusionIndexes: { type: 'array' },
        bloodPressureSystolics: { type: 'array' },
        bloodPressureDiastolics: { type: 'array' },
        casualWalkDurationsPerDay: { type: 'array' },
      },
    },
  },
}

export async function getPatientWithStats(params: { startTimestamp: number; endTimestamp: number }) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  const queryExpression = q => {
    const expression = q.findRecords(ModelTypes.PatientWithStats)
    return expression.filter(
      { attribute: 'starttimestamp', value: params.startTimestamp },
      { attribute: 'endtimestamp', value: params.endTimestamp }
    )
  }

  return remote.query(queryExpression).catch(e => {
    console.log('fetch failed in patients with events' + JSON.stringify(e))
  })
}

export async function getPatientWithStatsById(params: {
  patientId: string
  startTimestamp: number | undefined
  endTimestamp: number | undefined
}) {
  const schema = new Schema(schemaDefinition)
  const remote = await KC.getJsonApiRemoteResource(schema)
  const queryExpression = q => {
    const expression = q.findRecords(ModelTypes.PatientWithStats)
    return expression.filter(
      { attribute: 'id', value: params.patientId },
      { attribute: 'starttimestamp', value: params.startTimestamp },
      { attribute: 'endtimestamp', value: params.endTimestamp }
    )
  }

  return remote.query(queryExpression).catch(e => {
    console.log('fetch failed in patients with events' + JSON.stringify(e))
  })
}
